import React, { useState, useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { schoolsAPI } from '../../api';

export default function AddStudentModal({ onClose, onAdd, editingStudent = null, isEditing = false, schoolContext = null }) {
  const { getAccessTokenSilently } = useAuth0();
  
  const [formData, setFormData] = useState({
    first_name: editingStudent?.first_name || '',
    last_name: editingStudent?.last_name || '',
    email: editingStudent?.email || '',
    school_id: editingStudent?.school_id || schoolContext?.id || '' // Use school context if available
  });

  const [errors, setErrors] = useState({});
  
  // School management state
  const [schools, setSchools] = useState([]);
  const [loadingSchools, setLoadingSchools] = useState(true);
  const [schoolsError, setSchoolsError] = useState(null);

  // Fetch schools on component mount
  useEffect(() => {
    const fetchSchools = async () => {
      try {
        setLoadingSchools(true);
        setSchoolsError(null);
        const token = await getAccessTokenSilently();
        
        const schoolsData = await schoolsAPI.getSchools(token); // Fixed: pass token as first parameter
        setSchools(schoolsData);
        
        // Auto-select school with priority: editing student's school > school context > first school
        if (schoolsData.length > 0) {
          // Use the string ID from T_ATT_SCHOOL (not the old integer id)
          const schoolId = editingStudent?.school_id || schoolContext?.id || schoolsData[0].id; // T_ATT_SCHOOL.id is already a string
          console.log('🏫 AddStudentModal: Setting school_id:', schoolId, typeof schoolId);
          setFormData(prev => ({
            ...prev,
            school_id: schoolId
          }));
        }
      } catch (error) {
        console.error('Failed to fetch schools:', error);
        setSchoolsError(error.message);
        // Fallback to default behavior with manual school ID
        setSchools([]);
      } finally {
        setLoadingSchools(false);
      }
    };

    fetchSchools();
  }, [getAccessTokenSilently, editingStudent?.school_id, schoolContext?.id]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.first_name.trim()) {
      newErrors.first_name = 'First name is required';
    }
    
    if (!formData.last_name.trim()) {
      newErrors.last_name = 'Last name is required';
    }
    
    if (!formData.school_id) {
      newErrors.school_id = 'Please select a school';
    }

    // Email validation (optional but must be valid format if provided)
    if (formData.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) {
      newErrors.email = 'Please enter a valid email address';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (validateForm()) {
      // Transform the data to match what the parent component expects
      const studentData = {
        name: `${formData.first_name} ${formData.last_name}`,
        first_name: formData.first_name,
        last_name: formData.last_name,
        email: formData.email.trim() || undefined, // Include email, undefined if empty
        school_id: formData.school_id // Keep as string for T_ATT_SCHOOL
        // organization_id is now automatically handled by JWT middleware
      };
      
      console.log('🎓 AddStudentModal: Submitting student data:', studentData);
      console.log('🎓 AddStudentModal: school_id type:', typeof studentData.school_id);
      onAdd(studentData);
    }
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  // Handle Enter key navigation between fields
  const handleKeyDown = (e, nextFieldName) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      
      if (nextFieldName === 'submit') {
        // On the last field, submit the form
        handleSubmit(e);
      } else {
        // Move to next field - try by name first, then by ID
        let nextField = document.querySelector(`[name="${nextFieldName}"]`);
        if (!nextField) {
          nextField = document.getElementById(nextFieldName);
        }
        if (nextField) {
          nextField.focus();
        }
      }
    }
  };

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      onClick={handleOverlayClick}
    >
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
          <h2 className="text-xl font-bold text-slate-800">
            {isEditing ? 'Edit Student' : 'Add New Student'}
          </h2>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-600 transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6">
          <div className="space-y-4">
            {/* First Name */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                First Name *
              </label>
              <input
                type="text"
                name="first_name"
                value={formData.first_name}
                onChange={handleChange}
                onKeyDown={(e) => handleKeyDown(e, 'last_name')}
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.first_name ? 'border-red-300' : 'border-slate-300'
                }`}
                placeholder="Enter student's first name"
              />
              {errors.first_name && (
                <p className="mt-1 text-sm text-red-600">{errors.first_name}</p>
              )}
            </div>

            {/* Last Name */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Last Name *
              </label>
              <input
                type="text"
                name="last_name"
                value={formData.last_name}
                onChange={handleChange}
                onKeyDown={(e) => handleKeyDown(e, 'email')}
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.last_name ? 'border-red-300' : 'border-slate-300'
                }`}
                placeholder="Enter student's last name"
              />
              {errors.last_name && (
                <p className="mt-1 text-sm text-red-600">{errors.last_name}</p>
              )}
            </div>

            {/* Email */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Email
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                onKeyDown={(e) => handleKeyDown(e, 'school_id')}
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.email ? 'border-red-300' : 'border-slate-300'
                }`}
                placeholder="Enter student's email address (optional)"
              />
              {errors.email && (
                <p className="mt-1 text-sm text-red-600">{errors.email}</p>
              )}
            </div>

            {/* School Dropdown */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                School *
              </label>
              {loadingSchools ? (
                <div className="w-full px-3 py-2 border border-slate-300 rounded-lg bg-slate-50 flex items-center">
                  <svg className="w-4 h-4 animate-spin mr-2" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <span className="text-slate-500">Loading schools...</span>
                </div>
              ) : schoolsError ? (
                <div className="space-y-2">
                  <div className="w-full px-3 py-2 border border-red-300 rounded-lg bg-red-50 text-red-600 text-sm">
                    Failed to load schools: {schoolsError}
                  </div>
                  <input
                    type="number"
                    name="school_id"
                    value={formData.school_id}
                    onChange={handleChange}
                    onKeyDown={(e) => handleKeyDown(e, 'submit')}
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      errors.school_id ? 'border-red-300' : 'border-slate-300'
                    }`}
                    placeholder="Enter school ID (fallback)"
                    min="1"
                  />
                </div>
              ) : schools.length === 0 ? (
                <div className="w-full px-3 py-2 border border-slate-300 rounded-lg bg-slate-50 text-slate-500">
                  No schools available
                </div>
              ) : schoolContext ? (
                // When in school context, show only the current school and disable selection
                <div 
                  id="school_id"
                  tabIndex={0}
                  onKeyDown={(e) => handleKeyDown(e, 'submit')}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg bg-slate-50 text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {schoolContext.name} (school-specific)
                </div>
              ) : (
                <select
                  name="school_id"
                  value={formData.school_id}
                  onChange={handleChange}
                  onKeyDown={(e) => handleKeyDown(e, 'submit')}
                  className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors.school_id ? 'border-red-300' : 'border-slate-300'
                  }`}
                >
                  <option value="">Select a school</option>
                  {schools.map((school) => (
                    <option key={school.id} value={school.id}>
                      {school.name}
                    </option>
                  ))}
                </select>
              )}
              {errors.school_id && (
                <p className="mt-1 text-sm text-red-600">{errors.school_id}</p>
              )}
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end space-x-3 mt-6 pt-4 border-t border-slate-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
            >
              {isEditing ? 'Update Student' : 'Add Student'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}